/**
 * Custom hook for Dietician Schedule Form logic
 *
 * Handles Calendly OAuth integration, polling, form validation,
 * and submission logic for the dietician schedule form.
 *
 * @module hooks/useDieticianScheduleForm
 */

import { useState, useCallback, useMemo, useEffect, useRef } from 'react';
import { scrollToFirstErrorField } from '@/utils/scrollToError';
import { apiService } from '@/services/ApiService';
import { UserApiError } from '@/services/domains/UserDomain';
import { getLogger } from '@/services/shared/LoggerFactory';

/**
 * Form data interface for Step 3
 */
export interface DieticianScheduleFormData {
  calendlySchedulingUri: string | null;
}

export interface UseDieticianScheduleFormProps {
  onSubmit: (formData: DieticianScheduleFormData) => Promise<void>;
  initialData?: Partial<DieticianScheduleFormData>;
}

export interface UseDieticianScheduleFormReturn {
  // Form state
  formData: DieticianScheduleFormData;
  showLoadingOverlay: boolean;
  isPolling: boolean;

  // Computed values
  isFormValid: boolean;
  validationState: Record<string, { touched?: boolean; result: { isValid: boolean; error?: string } }>;
  hasValidationError: boolean;
  shouldShowValidationErrors: boolean;

  // Event handlers
  handleCalendlyConnect: () => void;
  handleSubmit: (event: React.FormEvent) => Promise<void>;
  scrollToFirstError: () => void;

  // Field refs for scroll-to-error functionality
  fieldRefs: React.RefObject<Record<string, HTMLElement | null>>;
}

/**
 * Custom hook for managing Dietician Schedule Form state and logic
 */
export const useDieticianScheduleForm = ({
  onSubmit,
  initialData
}: UseDieticianScheduleFormProps): UseDieticianScheduleFormReturn => {
  const logger = getLogger();

  // Field refs for scroll-to-error functionality
  const fieldRefs = useRef<Record<string, HTMLElement | null>>({});

  // Form state
  const [formData, setFormData] = useState<DieticianScheduleFormData>({
    calendlySchedulingUri: initialData?.calendlySchedulingUri || null,
  });

  // Loading overlay state
  const [showLoadingOverlay, setShowLoadingOverlay] = useState(false);

  // Polling state
  const [isPolling, setIsPolling] = useState(false);
  const pollingIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const popupCheckIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const validationScrollTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  // State to track when validation errors should be shown
  const [shouldShowValidationErrors, setShouldShowValidationErrors] = useState(false);

  // Update form data when initialData changes
  useEffect(() => {
    logger.debug('initialData?.calendlySchedulingUri, ', initialData?.calendlySchedulingUri);
    if (initialData?.calendlySchedulingUri !== undefined) {
      setFormData(prev => ({
        ...prev,
        calendlySchedulingUri: initialData.calendlySchedulingUri || null,
      }));
    }
  }, [initialData]);

  // Function to check if calendar integration was successful
  const checkCalendarStatus = useCallback(async () => {
    try {
      const schedulingUri = await apiService.user.getCalendlyCalendar();
      // Calendar integration found - success!
      logger.log('Calendly integration successful:', schedulingUri);

      // Update form state to reflect successful connection
      setFormData(prev => ({
        ...prev,
        calendlySchedulingUri: schedulingUri
      }));

      logger.log('Calendar connected successfully!');

      // Stop polling when calendar is successfully connected
      if (pollingIntervalRef.current) {
        clearInterval(pollingIntervalRef.current);
        pollingIntervalRef.current = null;
        setIsPolling(false);
      }

      return true; // Indicate success
    } catch (error) {
      // Handle specific error cases
      if (error instanceof UserApiError && error.status === 404) {
        // No calendar integration found - may have been cancelled or failed
        logger.warn('Calendar integration not found - may have been cancelled');
      } else {
        logger.error('Error checking calendar status:', error);
      }
      return false; // Indicate failure/not found
    }
  }, []);

  // Function to start polling calendar status
  const startPolling = useCallback(() => {
    if (pollingIntervalRef.current) {
      return; // Already polling
    }

    logger.debug('Starting calendar status polling every 2.5 seconds');
    setIsPolling(true);

    pollingIntervalRef.current = setInterval(async () => {
      const success = await checkCalendarStatus();
      if (success) {
        // Calendar connected, polling will be stopped by checkCalendarStatus
        logger.log('Polling stopped - calendar connected');
      }
    }, 2500); // Poll every 2.5 seconds
  }, [checkCalendarStatus]);

  // Function to stop polling
  const stopPolling = useCallback(() => {
    if (pollingIntervalRef.current) {
      logger.log('Stopping calendar status polling');
      clearInterval(pollingIntervalRef.current);
      pollingIntervalRef.current = null;
      setIsPolling(false);
    }
  }, []);

  // Cleanup polling on component unmount
  useEffect(() => {
    return () => {
      stopPolling();

      if (popupCheckIntervalRef.current) {
        clearInterval(popupCheckIntervalRef.current);
        popupCheckIntervalRef.current = null;
      }

      if (validationScrollTimeoutRef.current) {
        clearTimeout(validationScrollTimeoutRef.current);
        validationScrollTimeoutRef.current = null;
      }
    };
  }, [stopPolling]);

  // Handle Calendly OAuth connection
  const handleCalendlyConnect = useCallback(() => {
    const clientId = import.meta.env.VITE_CALENDLY_CLIENT_ID;
    const redirectUri = import.meta.env.VITE_CALENDLY_REDIRECT_URI;

    if (!clientId || !redirectUri) {
      logger.error('Calendly OAuth configuration missing');
      return;
    }

    // Show loading overlay
    setShowLoadingOverlay(true);

    // Start polling for calendar status immediately when button is clicked
    startPolling();

    // Initiate Calendly OAuth flow in popup window
    const calendlyAuthUrl = `https://auth.calendly.com/oauth/authorize?` +
      `client_id=${clientId}&` +
      `response_type=code&` +
      `redirect_uri=${encodeURIComponent(redirectUri)}`;

    // Open OAuth flow in popup window
    const popup = window.open(
      calendlyAuthUrl,
      'calendly-oauth',
      'width=500,height=600,scrollbars=yes,resizable=yes'
    );

    // Function to handle popup close
    const handlePopupClose = () => {
      logger.log('Popup window closed');
      // Hide loading overlay
      setShowLoadingOverlay(false);
      // Note: Don't stop polling here - let it continue until calendar is connected
    };

    // Check if popup is closed
    const checkClosed = setInterval(() => {
      if (popup?.closed) {
        clearInterval(checkClosed);
        popupCheckIntervalRef.current = null;
        logger.log('Popup window closed');
        handlePopupClose();
      }
    }, 1000);
    popupCheckIntervalRef.current = checkClosed;
  }, [startPolling]);

  // Computed values
  const isFormValid = useMemo(() => {
    return formData.calendlySchedulingUri !== null && formData.calendlySchedulingUri !== undefined;
  }, [formData.calendlySchedulingUri]);

  // Create validation state for scroll-to-error
  const validationState = useMemo(() => {
    const state: Record<string, { touched?: boolean; result: { isValid: boolean; error?: string } }> = {};

    // Add validation state for calendly connection
    const isCalendlyConnected = formData.calendlySchedulingUri !== null && formData.calendlySchedulingUri !== undefined;
    state['calendlyConnection'] = {
      touched: true,
      result: {
        isValid: isCalendlyConnected,
        error: !isCalendlyConnected ? 'Please connect your Calendly account to continue' : undefined
      }
    };

    return state;
  }, [formData.calendlySchedulingUri]);

  // Check if there's a validation error for the Calendly connection
  const hasValidationError = useMemo(() => {
    const calendlyValidation = validationState['calendlyConnection'];
    return calendlyValidation && !calendlyValidation.result.isValid;
  }, [validationState]);

  // Scroll to first error function
  const scrollToFirstError = useCallback(() => {
    setShouldShowValidationErrors(true);
    const fieldOrder = ['calendlyConnection'];
    scrollToFirstErrorField(fieldOrder, validationState, fieldRefs.current);
  }, [validationState]);

  // Form submission
  const handleSubmit = useCallback(async (event: React.FormEvent) => {
    event.preventDefault();

    logger.log('Form submission - calendlySchedulingUri: ', formData.calendlySchedulingUri);

    // Validate Calendly connection before submission
    const isCalendlyConnected = formData.calendlySchedulingUri !== null && formData.calendlySchedulingUri !== undefined;

    if (!isCalendlyConnected) {
      logger.log('Form validation failed: Calendly not connected');

      // Force validation state update and scroll to error
      if (validationScrollTimeoutRef.current) {
        clearTimeout(validationScrollTimeoutRef.current);
      }
      validationScrollTimeoutRef.current = setTimeout(() => {
        scrollToFirstError();
      }, 100);
      return;
    }

    try {
      logger.log('Form validation passed, submitting...');
      await onSubmit(formData);
    } catch (error) {
      // Error handling is managed by parent component
      logger.error('Schedule form submission error:', error);
    }
  }, [formData, onSubmit, scrollToFirstError]);

  return {
    // Form state
    formData,
    showLoadingOverlay,
    isPolling,

    // Computed values
    isFormValid,
    validationState,
    hasValidationError,
    shouldShowValidationErrors,

    // Event handlers
    handleCalendlyConnect,
    handleSubmit,
    scrollToFirstError,

    // Field refs
    fieldRefs
  };
};